import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageViewEnhanced } from '@/utils/analytics';
import metadataJson from '@/data/metatags.json';
import { useTranslation } from 'react-i18next';

// Type for our metadata
interface MetadataItem {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogUrl: string;
  ogType: string;
  twitterCard: string;
  canonical: string;
  keywords: string;
}

interface MetadataCollection {
  [path: string]: MetadataItem;
}

// Type assertion for the imported JSON
const metadata = metadataJson as MetadataCollection;

interface PageViewTrackerProps {
  /**
   * Optional path override. If not provided, will use current URL path
   */
  path?: string;
  
  /**
   * Optional title override. If not provided, will use title from metadata
   */
  title?: string;
}

/**
 * Component that tracks pageviews with enriched data like page title
 * Use this component on key pages where you want better analytics data
 */
const PageViewTracker: React.FC<PageViewTrackerProps> = ({ path, title }) => {
  const location = useLocation();
  const { t } = useTranslation('common');
  
  // Use provided path or current location path
  const currentPath = path || location.pathname;
  
  // Use root path as fallback if no matching path is found
  const metadataPath = metadata[currentPath] ? currentPath : '/';
  const meta = metadata[metadataPath];
  
  // Translation keys based on path
  const translationNamespace = metadataPath === '/' 
    ? 'homepage' 
    : metadataPath.replace('/', ''); // Remove leading slash
  
  // Get the appropriate title
  const pageTitle = title || 
    t(`${translationNamespace}.helmet.title`, { defaultValue: meta.title }) ||
    meta.title ||
    document.title;
  
  useEffect(() => {
    // Track pageview with title
    trackPageViewEnhanced(currentPath, pageTitle, location);
    
    // For debugging in development
    if (import.meta.env.DEV) {
      console.log('PageView tracked:', {
        path: currentPath,
        title: pageTitle,
        location: location.pathname + location.search
      });
    }
  }, [currentPath, pageTitle, location]);
  
  return null;
};

export default PageViewTracker;